import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const p = PRICING_2026.paint;

export const articleCostToPaintHouseExterior: Article = {
  slug: "cost-to-paint-house-exterior",
  title: "Exterior House Painting Cost: What Drives the Price and What the Prep Really Takes",
  description: "Professional exterior painting runs $2,500–$6,500 for a typical home. Prep work, washing, scraping, caulking, and priming, accounts for roughly 60% of the labor cost. Here's how to budget accurately and what to confirm before signing.",
  category: "Painting",
  tags: ["Exterior Painting", "Cost Guide", "DIY", "Home Maintenance", "Curb Appeal"],
  createdAt: "June 27, 2026",
  updatedAt: "July 3, 2026",
  lastVerified: "July 3, 2026",
  readTime: "8 min read",
  keyTakeaway: `Painting a house exterior costs $2,500-$6,000 professionally or $400-$800 for DIY. Two-story homes cost 40-60% more than single-story. Proper prep (washing, scraping, priming) accounts for 60% of the job, don't rush it.`,
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "Sherwin-Williams Professional Painting Guidelines",
      url: "https://www.sherwin-williams.com"
    },
    {
      name: "Paint Quality Institute - Exterior Painting Standards",
      url: "https://www.paintquality.com"
    }
  ],
  keyMetrics: [
    { label: "Professional (1-story)", value: "$2,500-$4,500" },
    { label: "Professional (2-story)", value: "$4,000-$6,500" },
    { label: "DIY Materials Only", value: "$400-$800" },
    { label: "Paint Coverage", value: `${p.coverageSqFtPerGallon} sq ft/gallon` }
  ],
  faqs: [
    {
      question: "How often should I paint my house exterior?",
      answer: "Wood siding needs repainting every 5-7 years. Fiber-cement lasts 10-15 years. Brick and stucco rarely need painting (every 15-20 years if painted). Watch for peeling, cracking, or fading: these are signs it's time, regardless of age."
    },
    {
      question: "Can I paint over old paint or do I need to strip it?",
      answer: "If the old paint is adhering well (no peeling or flaking), you can paint over it after cleaning and lightly sanding. If paint is peeling, you must scrape and prime bare spots. Severely deteriorated paint (alligatoring or chalking) requires full removal before repainting."
    },
    {
      question: "What's the best time of year to paint exterior?",
      answer: "Spring and fall are ideal, temperatures between 50-85°F, low humidity, and no rain forecast for 48 hours. Avoid painting in direct hot sunlight (paint dries too fast and doesn't adhere properly). Early morning starts work best in summer."
    }
  ],
  content: `
    <p>
      Exterior painting cost varies more than most homeowners expect, primarily because contractors price by paintable surface area, not by home square footage. A 2,000 SF home might have 3,500 SF of exterior wall and trim surface. The prep condition of that surface, whether it's clean and adhering or requires extensive scraping and priming, has as large an effect on the quote as the size of the home.
    </p>

    <div class="my-6 p-4 rounded-lg bg-surface border border-border-custom">
      <p class="font-bold text-foreground m-0 mb-2">The Short Version</p>
      <p class="text-sm text-text-muted m-0">
        Professional exterior painting costs $2,500–$4,500 for single-story, $4,000–$6,500 for two-story. DIY material costs run $400–$800 for a typical home, plus equipment rental ($200–$600). Prep work, pressure washing, scraping, caulking, and priming, is roughly 60% of the labor time in any exterior paint job. Spring and fall are the preferred seasons: 50–85°F, low humidity, no rain forecast for 48 hours.
      </p>
      <p class="text-xs text-text-muted mt-2 m-0">
        <em>Cost ranges from Meazora 2026 installer data. Application guidelines per Sherwin-Williams and Paint Quality Institute exterior painting standards.</em>
      </p>
    </div>

    <h2>What this article covers:</h2>
    <ul>
      <li>What drives cost differences between professional bids</li>
      <li>DIY material and equipment breakdown</li>
      <li>Why prep work determines how long the job lasts</li>
      <li>What to confirm before signing with a painting contractor</li>
    </ul>

    <h2>Professional Exterior Painting Costs (2026)</h2>
    <p>
      Hiring professionals costs $2,500-$6,500 for a typical 1,500-2,500 sq ft home. The wide range reflects several variables:
    </p>
    <ul>
      <li><strong>Single-story vs two-story:</strong> Two-story homes cost 40-60% more due to scaffolding, extended ladders, and increased labor time. Expect $2,500-$4,500 for single-story, $4,000-$6,500 for two-story.</li>
      <li><strong>Siding material:</strong> Wood and fiber-cement paint easily. Brick and stucco require special masonry paint and more labor. Vinyl siding often just needs washing, not painting (if you need replacement, check our <a href="/articles/siding-replacement-cost-guide/" class="text-[var(--accent)] hover:underline font-semibold">siding replacement cost guide</a>).</li>
      <li><strong>Condition of existing paint:</strong> If paint is peeling or chalky, prep work (scraping, sanding, priming) can add $500-$2,000 to the job.</li>
      <li><strong>Trim and details:</strong> Homes with extensive trim, shutters, or decorative elements cost more because cutting in around details takes time.</li>
      <li><strong>Paint quality:</strong> Premium paint costs $60-$90/gallon vs $35-$50 for mid-grade. Pros typically include this in the quote, but ask what's included.</li>
    </ul>
    <p>
      Most contractors quote by the square foot of paintable surface, not by home square footage. A 2,000 sq ft home might have 3,500 sq ft of exterior wall surface plus trim. Expect $1.50-$4.00 per sq ft of paintable surface, depending on prep needs and paint quality.
    </p>

    <h2>DIY Exterior Painting: What You're Really Getting Into</h2>
    <p>
      DIY painting costs $400-$800 in materials for a typical home. You can calculate your required amount with our <a href="/calculators/paint/" class="text-[var(--accent)] hover:underline font-semibold">paint calculator</a> or refer to our guide on <a href="/articles/how-much-paint-do-you-need-per-room/" class="text-[var(--accent)] hover:underline font-semibold">how much paint you need</a>. Here's the cost breakdown:
    </p>
    <ul>
      <li><strong>Paint:</strong> 15-25 gallons at $45-$75/gallon = $675-$1,875</li>
      <li><strong>Primer:</strong> 5-10 gallons at $35-$50/gallon = $175-$500</li>
      <li><strong>Supplies:</strong> Brushes, rollers, drop cloths, painter's tape, caulk = $150-$300</li>
      <li><strong>Equipment rental:</strong> Pressure washer ($50/day), scaffolding or lift ($150-$300/day) = $200-$600</li>
    </ul>
    <p>
      The material costs are straightforward. The hidden costs are time and risk:
    </p>
    <ul>
      <li><strong>Time commitment:</strong> A proper exterior paint job takes 3–5 full days for a DIYer working 8–10 hours/day. Prep is approximately 60% of the total labor, washing, scraping, sanding, caulking, and priming. Painting is the shorter portion of the job.</li>
      <li><strong>Physical demands:</strong> Extended work on ladders, overhead scraping, and hauling 5-gallon buckets at height are physically demanding. Two-story work requires either scaffolding or extended ladder positioning, renting scaffolding adds $150–$300/day but is significantly safer for sustained two-story work than ladder repositioning.</li>
      <li><strong>Quality risk:</strong> Poor prep leads to peeling within 2–3 years. Drips, lap marks, and uneven coverage are common DIY outcomes, particularly on the first exterior painting attempt.</li>
    </ul>



    <h2>Prep Work: The Difference Between a Job That Lasts and One That Doesn't</h2>
    <p>
      Here's what a proper prep sequence looks like:
    </p>
    <ol>
      <li><strong>Pressure washing:</strong> Remove dirt, mildew, and chalky residue. Let dry 24-48 hours.</li>
      <li><strong>Scraping:</strong> Remove all loose, peeling, or cracked paint. A putty knife works for large areas; a wire brush gets into corners.</li>
      <li><strong>Sanding:</strong> Feather the edges where old paint meets bare wood. This prevents visible ridges after painting.</li>
      <li><strong>Caulking:</strong> Seal gaps around windows, doors, and trim. Use paintable exterior caulk, not silicone.</li>
      <li><strong>Priming:</strong> Prime all bare wood and patched areas. Spot-prime is okay for small areas; full prime is better if over 30% of the surface is bare.</li>
      <li><strong>Painting:</strong> Apply two coats of quality exterior acrylic latex paint. Start from the top and work down to catch drips.</li>
    </ol>
    <p>
      Skipping any of these steps saves time now but costs you later. Paint applied over dirty, chalky, or unprimed surfaces will peel within 2-3 years.
    </p>

    <h2>DIY or Professional: What Determines the Better Choice</h2>
    <p>
      DIY exterior painting makes more sense when: the home is single-story with accessible walls, existing paint is adhering well with only minor prep needed, the painter has prior exterior experience, and 3–5 consecutive days are available. Equipment rental (pressure washer, scaffolding) is the main additional cost beyond materials.
    </p>
    <p>
      Professional painting is typically the more practical choice for two-story homes (scaffolding and ladder setup require significant time and safety coordination), severely deteriorated paint (professional prep equipment handles heavy scraping faster), and any situation where the painter isn't comfortable working at sustained heights.
    </p>

    <h2>Choosing the Right Paint</h2>
    <p>
      Not all exterior paints are created equal. Here's what to look for:
    </p>
    <ul>
      <li><strong>100% acrylic latex:</strong> Best for most siding types. Flexible, breathable, and UV-resistant.</li>
      <li><strong>Masonry paint:</strong> For brick, stucco, or concrete. Formulated to handle alkaline surfaces.</li>
      <li><strong>Oil-based:</strong> Only for metal surfaces or heavily chalked old paint. Less flexible than latex.</li>
      <li><strong>Sheen:</strong> Flat/matte hides imperfections but is harder to clean. Satin or low-luster offers a balance. Avoid high-gloss on large surfaces: it shows every imperfection.</li>
    </ul>
    <p>
      Premium paints ($60-$90/gallon) have more pigment, better binders, and last 2-3 years longer than budget paints. They're worth the upgrade for a job you won't want to repeat for a decade.
    </p>

    <h2>Getting Accurate Quotes</h2>
    <p>
      When soliciting quotes, ask for:
    </p>
    <ul>
      <li><strong>Written scope of work:</strong> What prep is included? How many coats? What brand and line of paint?</li>
      <li><strong>Surface area measurement:</strong> How many sq ft of paintable surface? Does this include trim?</li>
      <li><strong>Warranty:</strong> Most pros offer 2-5 year warranties on labor and materials.</li>
      <li><strong>Timeline:</strong> How many days? What's the weather contingency plan?</li>
      <li><strong>Insurance:</strong> Verify they carry liability and workers' comp insurance.</li>
    </ul>
    <p>
      Get three quotes. The lowest bid often cuts corners on prep or uses cheap paint. The highest bid might be over-insured or over-staffed. The middle bid is usually the sweet spot.
    </p>
  `
};